import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  AppBar,
  Modal,
  Toolbar,
  Button,
  Grid,
  Paper,
  Box,
  Tooltip,
  IconButton,
  Divider,
  Chip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Info modal field descriptions
const infoSections = [
  {
    title: 'Page Overview',
    color: '#0B1F3D',
    description:
      'The Add Fine page allows authorized users to define a new fine rule by selecting the fee type, month (fine for), date range, fine amount per day, and an optional remark. On successful save, the record is added and the user is navigated back.',
  },
  {
    title: 'Form Fields',
    color: '#1565C0',
    fields: [
      {
        label: 'Fee Type',
        desc: 'Dropdown to select the category of fee (e.g., Tuition Fee, Transport Fee) for which the fine is being defined. Options are fetched from the server.',
      },
      {
        label: 'Fine For',
        desc: 'Dropdown to select the month or period for which this fine rule applies. Options are fetched from the server.',
      },
      {
        label: 'Start Date',
        desc: 'The date from which the fine period begins. Fines will be applicable on or after this date.',
      },
      {
        label: 'End Date',
        desc: 'The date on which the fine period ends. Must be after the Start Date. The calendar automatically restricts selection to dates after the Start Date.',
      },
      {
        label: 'Fine Per Day',
        desc: 'The monetary amount charged as a fine for each day of delay within the defined date range. Cannot be a negative value.',
      },
      {
        label: 'Remark',
        desc: 'An optional free-text field to add any notes or context about this fine rule.',
      },
    ],
  },
  {
    title: 'Form Actions',
    color: '#2E7D32',
    fields: [
      {
        label: 'SAVE',
        desc: 'Submits the form data to the server. Dates are auto-formatted (DD/MM/YYYY) before sending. Shows a success modal on completion.',
      },
      {
        label: 'RESET',
        desc: 'Clears all form fields. On the first click, if any field is filled, it resets the form. On a second click (when form is already empty), it stays on the page.',
      },
    ],
  },
  {
    title: 'Validations',
    color: '#E65100',
    fields: [
      {
        label: 'End Date Check',
        desc: 'End Date must be strictly after the Start Date. A warning snackbar is shown if this rule is violated.',
      },
      {
        label: 'Fine Per Day Check',
        desc: 'Fine Per Day cannot be a negative number. A warning snackbar is shown if a negative value is entered.',
      },
    ],
  },
];

function AddFine() {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false); // State for Info Modal
  const navigate = useNavigate();
  const [feeType, setFeeType] = useState([]);
  const [month, setMonth] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState('');
  const [selectedValue, setSelectedValue] = useState("");
  const [selectedFee, setSelectedFee] = useState("");
  const [cancelClickCount, setCancelClickCount] = useState(0);
  const [formData, setFormData] = useState({
    feeTypeId: '',
    fineFor: '',
    startDate: '',
    endDate: '',
    finePerDay: '',
    remark: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "startDate") {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    } else if (name === "endDate") {
      if (formData.startDate && value > formData.startDate) {
        setFormData((prevData) => ({ ...prevData, [name]: value }));
      } else {
        // alert("End date must be after start date");
        setSnackbarMessage('End date must be after start date');
        setOpenSnackbar(true);
        setSnackbarSeverity("warning");
      }
    } else if (name === "finePerDay") {
      if (value >= 0) {
        setFormData((prevData) => ({ ...prevData, [name]: value }));
      } else {
        // alert("Fine per day cannot be negative");
        setSnackbarMessage('Fine per day cannot be negative');
        setOpenSnackbar(true);
        setSnackbarSeverity("warning");
      }
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);

      const formattedStartDate = formData.startDate.split('-').reverse().join('/');
      const formattedEndDate = formData.endDate.split('-').reverse().join('/');

      const updatedFormData = {
        ...formData,
        startDate: formattedStartDate,
        endDate: formattedEndDate
      };

      const response = await fetch(`${apiUrl}/Fine`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(updatedFormData),
      });

      if (response.ok) {
        setFormData({
          feeTypeId: '',
          fineFor: '',
          startDate: '',
          endDate: '',
          finePerDay: '',
          remark: '',
        });
        setSelectedFee('');
        setSelectedValue('');
        setCancelClickCount(0);
        setLoadingBarProgress(100);
        setShowSuccessModal(true);

      } else {
        setLoadingBarProgress(0);
        // alert('Failed to add fine');
        setSnackbarMessage('Failed to add fine');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error('API request error:', error);
      // alert('An error occurred. Please try again later.');
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const handleCancel = () => {
    const isFormFilled = Object.values(formData).some(value => value !== '') || selectedFee !== '' || selectedValue !== '';

    if (isFormFilled && cancelClickCount === 0) {
      setFormData({
        feeTypeId: '',
        fineFor: '',
        startDate: '',
        endDate: '',
        finePerDay: '',
        remark: '',
      });
      setSelectedFee('');
      setSelectedValue('');
      setCancelClickCount(1);
    } else {
      setCancelClickCount(0);

    }
    // navigate(-1);
  };

  const fetchddlFeeType = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/Fine/GetChargeType`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching financial years: ${response.status}`);
      }
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        throw new Error("Record Not Found");
      }
      setFeeType(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchMonth = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/Fine/GetMonthList`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        // alert(`Error fetching financial years: ${response.status}`);
        setSnackbarMessage(`Error fetching financial years: ${response.status}`);
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        throw new Error("Record Not Found");
      }
      setMonth(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchddlFeeType();
    fetchMonth();
  }, []);

  useEffect(() => {
    setFormData(prevData => ({
      ...prevData,
      feeTypeId: selectedFee,
      fineFor: selectedValue
    }));
  }, [selectedFee, selectedValue]);

  return (
    <Container>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold', flexGrow: 1 }}>
            Add Fine
          </Typography>
          {/* ── Info Button in AppBar ── */}
          <Tooltip title="View page information & field details" arrow>
            <IconButton
              onClick={() => setShowInfoModal(true)}
              sx={{
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '50%',
                width: 38,
                height: 38,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.28)',
                },
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6 }}>
        <Box elevation={3}>
          <Box p={3}>
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel htmlFor="feeTypeSelect">Fee Type</InputLabel>
                    <Select
                      id="selectedMonth"
                      label="Fee Type"
                      value={selectedFee}
                      onChange={(e) => setSelectedFee(e.target.value)}
                      fullWidth
                    >
                      <MenuItem value="">Select Month</MenuItem>
                      {feeType.map((item) => (
                        <MenuItem key={item.chargeTypeId} value={item.chargeTypeId}>
                          {item.chargeType}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="fineForSelect">Fine For</InputLabel>
                    <Select
                      id="selectedMonth"
                      label="Fine For"
                      value={selectedValue}
                      onChange={(e) => setSelectedValue(e.target.value)}
                      fullWidth
                    >
                      <MenuItem value="">Select Month</MenuItem>
                      {month.map((item) => (
                        <MenuItem key={item.monthId} value={item.monthId}>
                          {item.month}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    inputProps={{
                      min: formData.startDate
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Fine Per Day"
                    name="finePerDay"
                    value={formData.finePerDay}
                    onChange={handleInputChange}
                    inputProps={{
                      min: 0
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="text"
                    label="Remark"
                    name="remark"
                    value={formData.remark}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" color="primary" onClick={handleSubmit}>
                    SAVE
                  </Button>
                  <Button variant="contained" color="error" style={{ marginLeft: '10px' }} onClick={handleCancel}>
                    RESET
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Paper>

      {/* ── Success Modal ── */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Fine Added ✅
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              The Fine has been added successfully.
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1); // go back after closing modal
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ── Info Modal ── */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: 620 },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Info Modal Header */}
          <Box
            sx={{
              backgroundColor: '#0B1F3D',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#90CAF9', fontSize: 22 }} />
              <Typography id="info-modal-title" variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
                Add Fine — Page Information
              </Typography>
            </Box>
           
          </Box>

          {/* Info Modal Scrollable Body */}
          <Box sx={{ overflowY: 'auto', padding: '20px', flex: 1 }}>
            {infoSections.map((section, sIdx) => (
              <Box key={sIdx} sx={{ mb: 3 }}>
                {/* Section Title */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 4,
                      height: 22,
                      borderRadius: 1,
                      backgroundColor: section.color,
                    }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: section.color }}>
                    {section.title}
                  </Typography>
                </Box>

                {/* Overview text (no fields) */}
                {section.description && (
                  <Typography variant="body2" sx={{ color: '#555', pl: 1.5 }}>
                    {section.description}
                  </Typography>
                )}

                {/* Field rows */}
                {section.fields && (
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                    {section.fields.map((field, fIdx) => (
                      <Box key={fIdx}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            px: 2,
                            py: 1.2,
                            backgroundColor: fIdx % 2 === 0 ? '#fafafa' : '#fff',
                          }}
                        >
                          <Chip
                            label={field.label}
                            size="small"
                            sx={{
                              backgroundColor: section.color,
                              color: '#fff',
                              fontWeight: 'bold',
                              fontSize: '0.72rem',
                              minWidth: 120,
                              borderRadius: '4px',
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.6 }}>
                            {field.desc}
                          </Typography>
                        </Box>
                        {fIdx < section.fields.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </Box>
                )}

                {sIdx < infoSections.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Box>

          {/* Info Modal Footer */}
          <Box
            sx={{
              borderTop: '1px solid #e0e0e0',
              px: 2.5,
              py: 1.5,
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f9f9f9',
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{ backgroundColor: '#0B1F3D', '&:hover': { backgroundColor: '#163060' } }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default AddFine;